// Long-running runs of a project agent (ADR 0039 §2): start one, follow its event log as the worker writes it,
// answer the call it waits on, stop it. A run lives in a durable job: it survives a worker restart (the log says
// "resumed at step N") and pauses for approvals without holding a worker.
import type { LongRun, LongRunEvent } from '@cortex/sdk-agents-client';
import { cn } from '@cortex/ui';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { useAgentsHost, useAgentsKey, useAgentsQuery, useInvalidate } from './host.tsx';
import { OutcomeBadge } from './RecentRuns.tsx';
import { btnGhost, btnPrimary, input, messageOf } from './ui.tsx';

const LIVE = new Set(['queued', 'running']);
const STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  waiting: 'Waiting for you',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Stopped',
};

/** One event of the log in plain words. */
export function eventText(e: LongRunEvent): string {
  const d = (e.data ?? {}) as Record<string, unknown>;
  switch (e.type) {
    case 'queued':
      return `Queued by ${String(d.by ?? 'someone')}`;
    case 'started':
      return `Started (attempt ${String(d.attempt ?? 1)})`;
    case 'resumed':
      return `Resumed at step ${String(d.step ?? 0)} by a new worker (attempt ${String(d.attempt ?? '?')})${d.pending ? `, continuing the call of ${String(d.pending)}` : ''}`;
    case 'interrupted':
      return 'The worker stopped: the run goes back to the queue and continues from its last step';
    case 'tool_call':
      return `Calls ${String(d.name)}`;
    case 'tool_result':
      return `${String(d.name)} ${d.status === 'done' ? 'answered' : 'failed'}${d.summary ? `: ${String(d.summary).slice(0, 140)}` : ''}`;
    case 'usage':
      return `Model step: ${String(d.inputTokens ?? 0)} tokens in, ${String(d.outputTokens ?? 0)} out`;
    case 'approval':
      return `Asks your approval to run ${String(d.tool)}`;
    case 'waiting':
      return `Waiting for your answer on ${String(d.tool)}`;
    case 'approved':
      return `${String(d.by ?? 'Someone')} approved ${String(d.tool)}`;
    case 'declined':
      return `${String(d.by ?? 'Someone')} declined ${String(d.tool)}`;
    case 'cancelled':
      return `Stopped by ${String(d.by ?? 'someone')}`;
    case 'finished':
      return `Finished: ${String(d.outcome ?? d.status ?? '')}${d.error ? ` (${String(d.error)})` : ''}`;
    default:
      return e.type;
  }
}

function RunDetail({ run }: { run: LongRun }) {
  const host = useAgentsHost();
  const k = useAgentsKey();
  const invalidate = useInvalidate();
  const [state, setState] = React.useState<{ run: LongRun; events: LongRunEvent[] }>({ run, events: [] });
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const after = React.useRef(0);

  React.useEffect(() => {
    let stop = false;
    let t: ReturnType<typeof setTimeout> | undefined;
    after.current = 0;
    setState({ run, events: [] });
    const poll = async () => {
      try {
        const d = await host.client.longRun(run.id, after.current);
        if (stop) return;
        if (d.events.length) after.current = d.events[d.events.length - 1]!.seq;
        setState((s) => ({ run: d.run, events: [...s.events, ...d.events] }));
        setError('');
        if (LIVE.has(d.run.status)) t = setTimeout(() => void poll(), 1500);
        else invalidate([k('long-runs', run.agent)]);
      } catch (e) {
        if (stop) return;
        setError(messageOf(e));
        t = setTimeout(() => void poll(), 4000);
      }
    };
    void poll();
    return () => {
      stop = true;
      clearTimeout(t);
    };
    // The run's id and its moves between states (an answer queues it again) restart the follow.
  }, [run.id, run.status === 'queued' ? run.updatedAt : null]);

  const r = state.run;
  const act = async (f: () => Promise<LongRun>) => {
    setBusy(true);
    try {
      const next = await f();
      setState((s) => ({ ...s, run: next }));
      invalidate([k('long-runs', r.agent)]);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded border border-card-border p-3" data-testid="long-run-detail">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span data-testid="long-run-status" data-status={r.status} className="font-medium text-page-text">
          {STATUS_LABEL[r.status] ?? r.status}
        </span>
        {r.outcome && <OutcomeBadge outcome={r.outcome} stop={r.stop as never} testId="long-run-outcome" />}
        <span className="text-xs text-page-muted">
          step {r.step} · {r.toolCalls} tool calls · {r.attempts} {r.attempts === 1 ? 'leg' : 'legs'}
        </span>
        {LIVE.has(r.status) && <Loader2 size={12} className="animate-spin text-page-muted" />}
        {(LIVE.has(r.status) || r.status === 'waiting') && (
          <button
            type="button"
            className={cn(btnGhost, 'ml-auto')}
            disabled={busy}
            data-testid="long-run-cancel"
            onClick={() => void act(() => host.client.cancelLongRun(r.id))}
          >
            Stop
          </button>
        )}
      </div>
      <div className="text-xs text-page-muted">{r.message}</div>
      {r.status === 'waiting' && r.approval && (
        <div
          className="flex flex-wrap items-center gap-2 rounded bg-amber-50 p-2 text-sm dark:bg-amber-900/30"
          data-testid="long-run-approval"
        >
          <span>
            The agent asks to run <b>{r.approval.tool}</b> with{' '}
            <code className="text-xs">{JSON.stringify(r.approval.input)}</code>
          </span>
          <button
            type="button"
            className={btnPrimary}
            disabled={busy}
            data-testid="long-run-approve"
            onClick={() => void act(() => host.client.answerLongRun(r.id, r.approval!.key, true))}
          >
            Approve
          </button>
          <button
            type="button"
            className={btnGhost}
            disabled={busy}
            data-testid="long-run-decline"
            onClick={() => void act(() => host.client.answerLongRun(r.id, r.approval!.key, false))}
          >
            Decline
          </button>
        </div>
      )}
      {r.text && !LIVE.has(r.status) && r.status !== 'waiting' && (
        <div className="whitespace-pre-wrap text-sm text-page-text" data-testid="long-run-text">
          {r.text}
        </div>
      )}
      {error && <div className="text-xs text-red-600">{error}</div>}
      <ol className="m-0 flex list-none flex-col gap-0.5 p-0 text-xs" data-testid="long-run-events">
        {state.events.map((e) => (
          <li
            key={e.seq}
            data-testid="long-run-event"
            data-type={e.type}
            className={cn(
              'flex gap-2',
              (e.type === 'resumed' || e.type === 'interrupted') &&
                'font-medium text-amber-700 dark:text-amber-300',
            )}
          >
            <span className="shrink-0 tabular-nums text-page-muted">
              {new Date(e.at).toLocaleTimeString()}
            </span>
            <span className="text-page-text">{eventText(e)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** The agent's long-running runs (a project agent; started only when its harness mode is long-running). */
export function LongRuns({ agent, mode }: { agent: string; mode: string }) {
  const host = useAgentsHost();
  const k = useAgentsKey();
  const invalidate = useInvalidate();
  const q = useAgentsQuery(k('long-runs', agent), () => host.client.longRuns(agent));
  const [message, setMessage] = React.useState('');
  const [selected, setSelected] = React.useState<string | null>(null);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const runs = q.data?.runs ?? [];
  const longRunning = mode === 'long_running';
  if (!longRunning && !runs.length) return null;
  const current = runs.find((r) => r.id === selected) ?? null;

  const start = async () => {
    setBusy(true);
    setError('');
    try {
      const run = await host.client.startLongRun(agent, message.trim());
      setMessage('');
      setSelected(run.id);
      invalidate([k('long-runs', agent)]);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-2" data-testid="long-runs">
      <h3 className="m-0 text-sm font-medium text-page-text">Long-running runs</h3>
      <p className="m-0 text-xs text-page-muted">
        A long-running run executes in a background job: it saves its state after every step, continues on
        another worker when one restarts, and waits for you when a call needs your approval.
      </p>
      {longRunning ? (
        <div className="flex flex-col gap-2">
          <textarea
            className={cn(input, 'min-h-16')}
            placeholder="What should the agent do?"
            value={message}
            data-testid="long-run-message"
            onChange={(e) => setMessage(e.target.value)}
          />
          <div>
            <button
              type="button"
              className={btnPrimary}
              disabled={busy || !message.trim()}
              data-testid="long-run-start"
              onClick={() => void start()}
            >
              {busy ? 'Starting…' : 'Start a run'}
            </button>
          </div>
        </div>
      ) : (
        <p className="m-0 text-xs text-page-muted">Set the harness mode to long-running to start new runs.</p>
      )}
      {error && <div className="text-xs text-red-600">{error}</div>}
      {q.error && <div className="text-xs text-red-600">{q.error}</div>}
      {runs.length > 0 && (
        <ul className="m-0 list-none divide-y divide-divider p-0" data-testid="long-run-list">
          {runs.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                data-testid="long-run-item"
                data-status={r.status}
                className={cn(
                  'flex w-full items-center gap-2 py-1.5 text-left text-xs hover:bg-card-border/20',
                  r.id === selected && 'bg-card-border/30',
                )}
                onClick={() => setSelected(r.id === selected ? null : r.id)}
              >
                <span className="w-28 shrink-0 font-medium text-page-text">
                  {STATUS_LABEL[r.status] ?? r.status}
                </span>
                <span className="min-w-0 flex-1 truncate text-page-muted">{r.message}</span>
                {r.outcome && <OutcomeBadge outcome={r.outcome} stop={r.stop as never} />}
                <span className="shrink-0 tabular-nums text-page-muted">
                  {new Date(r.createdAt).toLocaleString()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {current && <RunDetail run={current} />}
    </section>
  );
}
